import i18next from 'i18next';
import HttpBackend from 'i18next-http-backend';
import ICU from 'i18next-icu';
import { initReactI18next, useTranslation as useReactI18nextTranslation } from 'react-i18next';
import { LocaleKeys } from "./LocaleKeys";
if (process.env.NODE_ENV === 'development') {
  // Tests: Load translations synchronously (no HTTP, no Suspense needed)
  const translations = require("../../locales/en.json");
  i18next.use(ICU).use(initReactI18next).init({
    lng: 'en',
    fallbackLng: 'en',
    supportedLngs: ['en'],
    resources: {
      en: {
        translation: translations
      }
    },
    initImmediate: false,
    interpolation: {
      escapeValue: false
    },
    react: {
      useSuspense: false
    }
  });
} else {
  // Production: Lazy-load translations via HTTP
  // App is wrapped in Suspense to wait for translations before rendering
  i18next.use(HttpBackend).use(ICU).use(initReactI18next).init({
    lng: 'en',
    fallbackLng: 'en',
    supportedLngs: ['en'],
    backend: {
      loadPath: '/locales/{{lng}}.json'
    },
    interpolation: {
      escapeValue: false
    },
    // Enable Suspense - app waits for translations before rendering
    react: {
      useSuspense: true
    },
    saveMissing: process.env.NODE_ENV === 'development',
    missingKeyHandler: (_lng, _ns, key) => {
      // eslint-disable-next-line no-console
      console.warn("[i18n] Missing translation key: ".concat(key));
    }
  });
}

/**
 * Direct t function for use outside React components.
 * Use this in utility functions, services, and other non-React code.
 *
 * WARNING: This does NOT integrate with Suspense. If used before translations
 * are loaded, it may return the key instead of the translated text.
 *
 * @example
 * import { t } from '~/i18n';
 * const error = t.errors.unexpected();
 * const plural = t.test.pluralization({ count: 5, gender: 'male', item: 'cat', items: 'cats' });
 */
export const t = LocaleKeys(i18next.t.bind(i18next));

// Utility types for nested path access

/**
 * Utility type to get the translation texts type for a given path.
 * Use this to type utility function parameters that receive translations.
 *
 * @example
 * import { LocaleKeyTexts } from '~/i18n';
 *
 * export const uploadErrorTextsPath = 'invoice.attachments.uploadErrors' as const;
 *
 * function getUploadErrorMessage(code: string, texts: LocaleKeyTexts<typeof uploadErrorTextsPath>): string {
 *     // texts is fully typed with the translation functions at that path
 * }
 */

/**
 * Hook for use in React components. Provides Suspense integration AND typed translations.
 *
 * This is the recommended way to use translations in React components:
 * - Integrates with React Suspense (waits for translations to load)
 * - Provides full type safety for translation keys and ICU arguments
 *
 * @example
 * import { useLocaleKeys } from '~/i18n';
 *
 * function MyComponent() {
 *     const { t } = useLocaleKeys();
 *     return <div>{t.errors.unexpected()}</div>;
 * }
 *
 * // With a sub-namespace path:
 * function SetupWidget() {
 *     const { t } = useLocaleKeys('dashboardPage.setupWidget');
 *     return <div>{t.uploadLogo.title()}</div>;
 * }
 */
export function useLocaleKeys(path) {
  const {
    i18n,
    ready
  } = useReactI18nextTranslation();
  const fullT = LocaleKeys(i18n.t.bind(i18n));
  const getNestedValue = (obj, pathStr) => {
    return pathStr.split('.').reduce((acc, key) => acc === null || acc === void 0 ? void 0 : acc[key], obj);
  };
  return {
    t: path ? getNestedValue(fullT, path) : fullT,
    i18n,
    ready
  };
}
export default i18next;